import { type VoiceSettings, loadSettings } from '../settings/voiceSettings';
import { fetchApi } from './api';
import { startSession, stopAll, isActiveSession, registerAudioElement, getAbortSignal, stopContext, logTtsEvent } from './ttsController';

import { openDB } from 'idb';

export async function clearCache() {
    const db = await openDB('tts-cache', 1);
    await db.clear('audio');
}

async function getCachedAudio(key: string): Promise<Blob | null> {
    const db = await openDB('tts-cache', 1, { upgrade(db) { db.createObjectStore('audio'); } });
    return await db.get('audio', key);
}

async function cacheAudio(key: string, blob: Blob) {
    const db = await openDB('tts-cache', 1);
    await db.put('audio', blob, key);
}

function generateCacheKey(settings: VoiceSettings, text: string) {
    const textLen = text.length;
    const preview = text.slice(0, 20);
    const p = settings.provider;
    let details = '';

    if (p === 'openai') details = `${settings.openai.model}-${settings.openai.voice}`;
    if (p === 'elevenlabs') details = `${settings.elevenlabs.modelId}-${settings.elevenlabs.voiceId}-${settings.elevenlabs.stability}`;
    if (p === 'deepgram') details = `${settings.deepgram.model}-${settings.deepgram.speed}`;
    if (p === 'piper') details = `${settings.piper.voiceId}`;

    return `${p}:${details}:${textLen}:${preview}`;
}

function sanitizeForSpeech(text: string): string {
    return text.replace(/<[^>]*>/g, '').replace(/[\r\n]+/g, ' ').trim();
}

function sanitizePlaybackRate(rate: any): number {
    const r = Number(rate);
    if (!Number.isFinite(r)) return 1.0;
    return Math.max(0.1, Math.min(r, 10.0));
}

export { stopAll as stopSpeaking };
export function stopChat() { stopContext('chat'); }
export function stopReader() { stopContext('reader'); }

async function fetchAudioBlob(text: string, settings: VoiceSettings, sid: number): Promise<Blob | null> {
    if (!text.trim()) return null;
    const signal = getAbortSignal(sid);
    const cacheKey = generateCacheKey(settings, text);

    if (settings.cacheAudio) {
        const cached = await getCachedAudio(cacheKey);
        if (cached) {
            logTtsEvent('TTS_REQUEST', {
                sessionId: sid, provider: settings.provider, model: getModelName(settings),
                speed: getSpeed(settings), textLen: text.length, cacheHit: true
            });
            return cached;
        }
    }

    if (settings.provider === 'free') return null;

    logTtsEvent('TTS_REQUEST', {
        sessionId: sid, provider: settings.provider, model: getModelName(settings),
        speed: getSpeed(settings), textLen: text.length, cacheHit: false
    });

    let blob: Blob | null = null;
    if (settings.provider === 'openai') blob = await fetchOpenAI(text, settings, signal);
    else if (settings.provider === 'elevenlabs') blob = await fetchElevenLabs(text, settings, signal);
    else if (settings.provider === 'deepgram') blob = await fetchDeepgram(text, settings, signal);
    else if (settings.provider === 'piper') blob = await generatePiper(text, settings);

    if (blob && settings.cacheAudio) {
        await cacheAudio(cacheKey, blob);
    }
    return blob;
}

function getModelName(s: VoiceSettings): string {
    if (s.provider === 'openai') return s.openai.model;
    if (s.provider === 'elevenlabs') return s.elevenlabs.modelId;
    if (s.provider === 'deepgram') return s.deepgram.model;
    return 'default';
}

function getSpeed(s: VoiceSettings): number {
    if (s.provider === 'openai') return s.openai.speed;
    if (s.provider === 'elevenlabs') return s.elevenlabs.speed;
    if (s.provider === 'deepgram') return s.deepgram.speed;
    if (s.provider === 'piper') return s.piper.speed;
    return 1.0;
}

export async function speakPhrase(text: string, sessionId?: number, onEnd?: (err?: any) => void, overrideSettings?: VoiceSettings) {
    const sid = sessionId ?? startSession('single-phrase');
    if (!isActiveSession(sid)) return;

    const settings = overrideSettings || loadSettings();
    const cleanText = sanitizeForSpeech(text);
    const provider = settings.provider || 'free';

    console.log(`[TTS] TTS Request: sid=${sid} provider=${provider} charLen=${cleanText.length} cache=${settings.cacheAudio}`);
    logTtsEvent('CHUNK_START', { sessionId: sid, provider, len: cleanText.length });

    // Diagnostics
    (window as any).__LAST_TTS_EVENT__ = JSON.stringify({
        sessionId: sid,
        provider,
        state: 'started',
        textLen: cleanText.length,
        ts: new Date().toISOString()
    });

    try {
        if (provider === 'free') {
            speakFree(cleanText, settings, sid, onEnd);
        } else {
            const blob = await fetchAudioBlob(cleanText, settings, sid);

            if (!blob) throw new Error('No audio generated');
            if (!isActiveSession(sid)) return;

            let playbackSpeed = 1.0;
            if (provider === 'openai') playbackSpeed = settings.openai.speed;
            else if (provider === 'elevenlabs') playbackSpeed = settings.elevenlabs.speed;
            else if (provider === 'deepgram') playbackSpeed = settings.deepgram.speed;
            else if (provider === 'piper') playbackSpeed = settings.piper.speed;

            playAudioBlob(blob, sid, playbackSpeed, onEnd);
        }
    } catch (e: any) {
        if (!isActiveSession(sid)) return;
        console.error('TTS Error:', e);

        (window as any).__LAST_TTS_EVENT__ = JSON.stringify({ state: 'error', err: e.message });

        logTtsEvent('ERROR', { sessionId: sid, error: e.message });
        if (onEnd) onEnd(e);
    }
}

function speakFree(text: string, settings: VoiceSettings, sid: number, onEnd?: (err?: any) => void) {
    const synth = window.speechSynthesis;
    if (!synth) {
        if (onEnd) onEnd();
        return;
    }

    const _speak = (attemptsLeft: number) => {
        if (!isActiveSession(sid)) return;

        const voices = synth.getVoices();

        if (voices.length === 0 && attemptsLeft > 0) {
            console.log('TTS: Voices empty, waiting...');
            const onVoicesChanged = () => {
                synth.removeEventListener('voiceschanged', onVoicesChanged);
                _speak(attemptsLeft - 1);
            };
            synth.addEventListener('voiceschanged', onVoicesChanged);

            setTimeout(() => {
                synth.removeEventListener('voiceschanged', onVoicesChanged);
                if (window.speechSynthesis.getVoices().length === 0 && attemptsLeft > 1) {
                    _speak(attemptsLeft - 1);
                } else {
                    doSpeak(voices);
                }
            }, 500);
            return;
        }

        doSpeak(voices);
    };

    const doSpeak = (voices: SpeechSynthesisVoice[]) => {
        if (!isActiveSession(sid)) return;

        const u = new SpeechSynthesisUtterance(text);
        const targetURI = settings.free.voiceURI;

        let voice: SpeechSynthesisVoice | undefined;

        if (targetURI) {
            voice = voices.find(v => v.voiceURI === targetURI);
            if (!voice) voice = voices.find(v => v.name === targetURI);
            if (!voice) voice = voices.find(v => v.voiceURI.includes(targetURI));
        }

        if (!voice) {
            voice = voices.find(v => v.lang.startsWith('en'));
        }

        if (voice) {
            u.voice = voice;
            u.lang = voice.lang;
            console.log(`TTS: Selected voice ${voice.name} (${voice.lang})`);
        } else {
            console.warn('TTS: No voice found, using system default.');
        }

        u.rate = settings.free.rate;
        u.pitch = settings.free.pitch;

        u.onend = () => {
            if (isActiveSession(sid) && onEnd) onEnd();
        };
        u.onerror = (e) => {
            if (isActiveSession(sid)) {
                console.error('TTS Error', e);
                if (onEnd) onEnd(e);
            }
        };

        synth.speak(u);
    };

    _speak(3);
}

async function fetchOpenAI(text: string, settings: VoiceSettings, signal: AbortSignal | null): Promise<Blob> {
    const s = settings.openai;
    const response = await fetchApi('/api/tts/openai', {
        method: 'POST',
        body: { apiKey: s.apiKey, model: s.model, input: text, voice: s.voice, speed: 1.0, response_format: s.format },
        signal
    });

    if (!response.ok) {
        let errText = response.statusText;
        try { errText = await response.text(); } catch { }
        throw new Error(`OpenAI error (${response.status}): ${errText.slice(0, 300)}`);
    }
    return await response.blob();
}

async function fetchElevenLabs(text: string, settings: VoiceSettings, signal: AbortSignal | null): Promise<Blob> {
    const s = settings.elevenlabs;
    const finalBody = {
        text: text,
        model_id: s.modelId,
        voice_settings: { stability: s.stability, similarity_boost: s.similarity },
        voice_id: s.voiceId,
        apiKey: (s.apiKey === '***' || s.apiKey === '•••') ? '' : s.apiKey
    };

    const response = await fetchApi('/api/tts/elevenlabs', {
        method: 'POST',
        body: finalBody,
        signal
    });

    if (!response.ok) {
        let errMsg = response.statusText;
        try {
            const errJson = await response.json();
            if (errJson && errJson.details && errJson.details.bodySnippet) {
                errMsg = errJson.details.bodySnippet;
            } else if (errJson && errJson.error) {
                errMsg = errJson.error;
            }
        } catch {
            try { errMsg = await response.text(); } catch { }
        }
        throw new Error(`ElevenLabs error (${response.status}): ${errMsg.slice(0, 300)}`);
    }
    return await response.blob();
}

async function fetchDeepgram(text: string, settings: VoiceSettings, signal: AbortSignal | null): Promise<Blob> {
    const s = settings.deepgram;
    const payload: any = {
        apiKey: (s.apiKey === '***' || s.apiKey === '•••') ? '' : s.apiKey,
        text,
        model: s.model,
        encoding: s.encoding,
        sample_rate: s.sampleRate
    };
    if (s.encoding === 'wav' || s.encoding === 'linear16') {
        payload.container = 'wav';
    }

    const response = await fetchApi('/api/tts/deepgram', {
        method: 'POST',
        body: payload,
        signal
    });

    if (!response.ok) {
        let errText = response.statusText;
        try { errText = await response.text(); } catch { }
        throw new Error(`Deepgram error (${response.status}): ${errText.slice(0, 300)}`);
    }
    return await response.blob();
}

async function generatePiper(_text: string, _settings: VoiceSettings): Promise<Blob> {
    throw new Error("Piper TTS is not enabled in this app.");
}

function playAudioBlob(blob: Blob, sid: number, speed: number, onEnd?: (err?: any) => void) {
    if (!isActiveSession(sid)) return;

    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    const safeSpeed = sanitizePlaybackRate(speed);

    if (Number.isFinite(safeSpeed)) {
        audio.playbackRate = safeSpeed;
    } else {
        audio.playbackRate = 1.0;
    }

    registerAudioElement(sid, audio);

    audio.onended = () => {
        URL.revokeObjectURL(url);
        if (isActiveSession(sid) && onEnd) onEnd();
    };
    audio.onerror = (e) => {
        console.error("Audio Playback Error", e);
        if (isActiveSession(sid) && onEnd) onEnd();
    };

    audio.play().catch(e => {
        console.warn("Autoplay blocked or failed", e);
        if (isActiveSession(sid) && onEnd) onEnd(e);
    });
}

export async function getCacheStats() {
    try {
        const db = await openDB('tts-cache', 1);
        const count = await db.count('audio');
        let size = 0;
        if (navigator.storage && navigator.storage.estimate) {
            const est = await navigator.storage.estimate();
            size = est.usage || 0;
        }
        return { count, size, type: 'IndexedDB' };
    } catch (e) {
        return { count: 0, size: 0, type: 'Error' };
    }
}

export interface SpeakOptions {
    onEnd?: (err?: any) => void;
    onProgress?: (index: number, text: string) => void;
    onStatusChange?: (status: 'playing' | 'buffering') => void;
    startIndex?: number;
    overrideSettings?: VoiceSettings;
}

export async function speakVerses(
    verses: string[],
    options: SpeakOptions = {}
) {
    const { onEnd, onProgress, onStatusChange, startIndex = 0, overrideSettings } = options;
    const sessionId = startSession('reader');
    logTtsEvent('READER_SPEAK_REQUESTED', { sessionId, verseCount: verses.length, startIndex });
    const settings = overrideSettings || loadSettings();
    const provider = settings.provider || 'free';

    let currentIndex = startIndex;
    const fetchQueue: Record<number, Promise<Blob | null>> = {};

    const ensureFetched = (idx: number) => {
        if (idx >= verses.length) return;
        if (!fetchQueue[idx]) {
            const clean = sanitizeForSpeech(verses[idx]);
            if (provider === 'free') {
                fetchQueue[idx] = Promise.resolve(null);
            } else {
                fetchQueue[idx] = fetchAudioBlob(clean, settings, sessionId).catch(err => {
                    console.warn(`[TTS] Prefetch failed for ${idx}`, err);
                    return null;
                });
            }
        }
    };

    const processNext = async () => {
        if (!isActiveSession(sessionId)) return;

        if (currentIndex >= verses.length) {
            logTtsEvent('PAGE_COMPLETE', { sessionId });
            if (onStatusChange) onStatusChange('playing');
            if (onEnd) onEnd();
            return;
        }

        const idx = currentIndex;
        ensureFetched(idx);
        ensureFetched(idx + 1);

        let blob: Blob | null = null;
        let didTimeOut = false;

        try {
            if (onStatusChange) onStatusChange('buffering');
            logTtsEvent('READER_FETCH_START', { sessionId, index: idx });

            const FETCH_TIMEOUT = 12000;
            const timeoutPromise = new Promise<null>((_, reject) =>
                setTimeout(() => {
                    didTimeOut = true;
                    reject(new Error('Fetch Timeout'));
                }, FETCH_TIMEOUT)
            );

            blob = await Promise.race([fetchQueue[idx], timeoutPromise]);

            logTtsEvent('READER_FETCH_DONE', { sessionId, index: idx, success: true });

        } catch (e: any) {
            if (didTimeOut) {
                console.error("[TTS] Watchdog: Reader stuck buffering.");
                logTtsEvent('READER_STUCK_BUFFERING', { sessionId, reason: 'timeout' });
            } else {
                logTtsEvent('READER_FETCH_DONE', { sessionId, index: idx, success: false, error: String(e) });
            }

            if (onStatusChange) onStatusChange('playing');
            if (onEnd) onEnd(e);
            return;
        } finally {
            if (!isActiveSession(sessionId)) {
                return;
            }
        }

        if (onStatusChange) onStatusChange('playing');

        if (onProgress) onProgress(idx, verses[idx]);

        logTtsEvent('READER_PLAY_START', { sessionId, index: idx });

        const onChunkEnd = (err?: any) => {
            logTtsEvent('READER_PLAY_END', { sessionId, index: idx });
            if (err) {
                if (onEnd) onEnd(err);
            } else {
                currentIndex++;
                processNext();
            }
        };

        if (provider === 'free') {
            const clean = sanitizeForSpeech(verses[idx]);
            speakFree(clean, settings, sessionId, onChunkEnd);
        } else {
            if (blob) {
                let playbackSpeed = getSpeed(settings);
                playAudioBlob(blob, sessionId, playbackSpeed, onChunkEnd);
            } else {
                const clean = sanitizeForSpeech(verses[idx]);
                if (clean) {
                    const err = new Error("Audio generation failed.");
                    console.error("[TTS] Aborting playback:", err);
                    if (onEnd) onEnd(err);
                    return;
                }
                currentIndex++;
                processNext();
            }
        }
    };

    processNext();
}
